import React, { useEffect, useState } from 'react';
import { DataTable } from '../components/DataTable';
import { WizardForm } from '../components/WizardForm';
import { StatusBadge } from '../components/StatusBadge';
import { UserIcon } from 'lucide-react';
import { beneficiaries as initialBeneficiaries } from '../data/beneficiaries';
// Helper to calculate age
const calculateAge = (dob: string) => {
  if (!dob) return '';
  const birthDate = new Date(dob);
  const today = new Date();
  let age = today.getFullYear() - birthDate.getFullYear();
  const m = today.getMonth() - birthDate.getMonth();
  if (m < 0 || m === 0 && today.getDate() < birthDate.getDate()) {
    age--;
  }
  return age.toString();
};
// ID Generation Helpers
const DEPT_CODE = '1';
const RANGE_CODES: Record<string, string> = {
  'Satkhira Range': '01',
  'Khulna Range': '02',
  'Chandpai Range': '03',
  'Sarankhola Range': '04'
};
const STATION_CODES: Record<string, string> = {
  'Burigoalini Station': '01',
  'Kaikhali Station': '02',
  'Dobeki Station': '03',
  'Nalian Station': '04',
  'Patkosta Station': '05',
  'Karamjal Station': '06',
  'Harbaria Station': '07',
  'Joymoni Station': '08',
  'Tamulbunia Station': '09',
  'Supati Station': '10',
  'Sharankhola Station': '11',
  'Kotka Station': '12'
};
const generateBeneficiaryId = (range: string, station: string, sequence: number) => {
  const rCode = RANGE_CODES[range] || '00';
  const sCode = STATION_CODES[station] || '00';
  const seq = String(sequence).padStart(5, '0');
  return `${DEPT_CODE}-${rCode}-${sCode}-${seq}`;
};
// Add generated IDs to initial data
const beneficiariesWithIds = initialBeneficiaries.map((b, index) => ({
  ...b,
  beneficiaryId: generateBeneficiaryId('Satkhira Range', 'Burigoalini Station', index + 1)
}));
export function BeneficiaryRegistry() {
  const [activeTab, setActiveTab] = useState<'list' | 'add' | 'approval'>('list');
  const [currentStep, setCurrentStep] = useState(0);
  const [beneficiariesList, setBeneficiariesList] = useState(beneficiariesWithIds);
  // Form State
  const [formData, setFormData] = useState({
    photo: '',
    fullName: '',
    fatherName: '',
    spouseName: '',
    nid: '',
    birthCertNo: '',
    dob: '',
    age: '',
    gender: '',
    education: '',
    professions: [] as string[],
    otherProfession: '',
    groupName: 'Eco Tour - Mangrove Trail',
    prevPermit: 'PER-2023-089, PER-2024-012',
    division: '',
    district: '',
    upazila: '',
    thana: '',
    union: '',
    village: '',
    range: '',
    station: '',
    bloodGroup: '',
    phone: '',
    email: '',
    nidAttachment: '',
    birthCertAttachment: '',
    educationAttachment: ''
  });
  // Auto-calculate age when DOB changes
  useEffect(() => {
    if (formData.dob) {
      setFormData(prev => ({
        ...prev,
        age: calculateAge(prev.dob)
      }));
    }
  }, [formData.dob]);
  const stationsByRange: Record<string, string[]> = {
    'Satkhira Range': ['Burigoalini Station', 'Kaikhali Station', 'Dobeki Station'],
    'Khulna Range': ['Nalian Station', 'Patkosta Station', 'Karamjal Station'],
    'Chandpai Range': ['Harbaria Station', 'Joymoni Station', 'Tamulbunia Station'],
    'Sarankhola Range': ['Supati Station', 'Sharankhola Station', 'Kotka Station']
  };
  const professionOptions = ['Crab Collector', 'Fish Collector', 'Honey Collector', 'Leaf Collector', 'Salt Water Collector', 'Sun Grass Collector', 'Others'];
  const handleProfessionChange = (prof: string) => {
    setFormData(prev => {
      const current = prev.professions;
      if (current.includes(prof)) {
        return {
          ...prev,
          professions: current.filter(p => p !== prof)
        };
      } else {
        return {
          ...prev,
          professions: [...current, prof]
        };
      }
    });
  };
  const pendingApprovals = [{
    photo: '👤',
    name: 'Nur Islam',
    nid: '6789012345678',
    profession: 'Fish Collector',
    village: 'Munshiganj',
    date: '2024-03-30'
  }, {
    photo: '👤',
    name: 'Habib Mollah',
    nid: '7890123456789',
    profession: 'Wood Collector',
    village: 'Gabura',
    date: '2024-03-29'
  }];
  const columns = [{
    key: 'beneficiaryId',
    label: 'Beneficiary ID'
  }, {
    key: 'name',
    label: 'Display Name'
  }, {
    key: 'email',
    label: 'Email ID'
  }, {
    key: 'nid',
    label: 'ID Number'
  }, {
    key: 'phone',
    label: 'Phone Number'
  }, {
    key: 'status',
    label: 'Status',
    render: (value: string) => <StatusBadge status={value as any} data-id="element-331" data-mp="13685v6kadr2a" />
  }];
  const approvalColumns = [{
    key: 'photo',
    label: 'Photo',
    render: (value: string) => <div className="w-10 h-10 bg-gray-200 rounded-full flex items-center justify-center text-xl" data-id="element-332" data-mp="aiufxsf09vro">
          {value}
        </div>
  }, {
    key: 'name',
    label: 'Name'
  }, {
    key: 'nid',
    label: 'NID'
  }, {
    key: 'profession',
    label: 'Profession'
  }, {
    key: 'village',
    label: 'Village'
  }, {
    key: 'date',
    label: 'Applied Date'
  }];
  const filters = [{
    key: 'profession',
    label: 'Profession',
    options: ['Fish Collector', 'Honey Collector', 'Wood Collector', 'Crab Collector']
  }, {
    key: 'status',
    label: 'Status',
    options: ['Approved', 'Pending', 'Rejected']
  }, {
    key: 'village',
    label: 'Village',
    options: ['Munshiganj', 'Gabura', 'Shyamnagar', 'Koyra', 'Dacope']
  }];
  const wizardSteps = [{
    title: 'Personal Info',
    description: 'Enter beneficiary personal information'
  }, {
    title: 'Address & Contact',
    description: 'Enter address and contact details'
  }, {
    title: 'Professional Details',
    description: 'Enter professional information'
  }, {
    title: 'Documents',
    description: 'Upload required documents'
  }, {
    title: 'Review & Submit',
    description: 'Review all information before submitting'
  }];
  return <div className="space-y-6" data-id="element-333" data-mp="3cfeuc3vgf121">
      <div data-id="element-334" data-mp="38e3donuk5q5z">
        <h1 className="text-2xl font-bold text-gray-900" data-id="element-335" data-mp="38emjj66xoo3n">
          Beneficiary Management
        </h1>
        <p className="text-sm text-gray-600 mt-1" data-id="element-336" data-mp="33egyi8v65zuj">
          Manage resource collectors (Jele, Moual, Bawali)
        </p>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200" data-id="element-337" data-mp="2yg5n3tp87ha7">
        <div className="flex gap-4" data-id="element-338" data-mp="2thm2bwmdcbjf">
          <button onClick={() => setActiveTab('list')} className={`px-4 py-2 border-b-2 font-medium text-sm transition-colors ${activeTab === 'list' ? 'border-[#1B5E20] text-[#1B5E20]' : 'border-transparent text-gray-600 hover:text-gray-900'}`} data-id="element-339" data-mp="2tidq4l9q54z5">
            Beneficiary List
          </button>
          <button onClick={() => setActiveTab('add')} className={`px-4 py-2 border-b-2 font-medium text-sm transition-colors ${activeTab === 'add' ? 'border-[#1B5E20] text-[#1B5E20]' : 'border-transparent text-gray-600 hover:text-gray-900'}`} data-id="element-340" data-mp="104nby8bqmtqa">
            Add Beneficiary
          </button>
          <button onClick={() => setActiveTab('approval')} className={`px-4 py-2 border-b-2 font-medium text-sm transition-colors ${activeTab === 'approval' ? 'border-[#1B5E20] text-[#1B5E20]' : 'border-transparent text-gray-600 hover:text-gray-900'}`} data-id="element-341" data-mp="h83yx7xtclog">
            Approval Queue ({pendingApprovals.length})
          </button>
        </div>
      </div>

      {/* Content */}
      {activeTab === 'list' && <DataTable columns={columns} data={beneficiariesList} filters={filters} onDataChange={setBeneficiariesList} searchPlaceholder="Search by name or NID..." data-id="element-342" data-mp="21n7ep5epi7cx" />}

      {activeTab === 'add' && <WizardForm steps={wizardSteps} currentStep={currentStep} onStepChange={step => {
      // Validation before moving to next step
      if (currentStep === 0 && step > 0) {
        if (!formData.nid && !formData.birthCertNo) {
          alert('Either NID or Birth Certificate No. is mandatory.');
          return;
        }
        if (formData.age && parseInt(formData.age) < 18) {
          alert('Age cannot be below 18.');
          return;
        }
      }
      setCurrentStep(step);
    }} onSubmit={() => {
      if (!formData.fullName || !formData.nid && !formData.birthCertNo || formData.professions.length === 0) {
        alert('Please fill in all required fields.');
        return;
      }
      if (parseInt(formData.age) < 18) {
        alert('Age cannot be below 18.');
        return;
      }
      const newId = generateBeneficiaryId(formData.range, formData.station, beneficiariesList.length + 1);
      const newBeneficiary = {
        beneficiaryId: newId,
        photo: '👤',
        name: formData.fullName,
        email: formData.email,
        nid: formData.nid || formData.birthCertNo,
        phone: formData.phone,
        profession: formData.professions.join(', '),
        village: formData.village,
        status: 'pending' as const,
        date: new Date().toISOString().split('T')[0]
      };
      setBeneficiariesList([newBeneficiary, ...beneficiariesList]);
      alert(`Beneficiary ${formData.fullName} registered successfully with ID ${newId} and sent for approval!`);
      setCurrentStep(0);
      setActiveTab('approval');
      // Reset form
      setFormData({
        photo: '',
        fullName: '',
        fatherName: '',
        spouseName: '',
        nid: '',
        birthCertNo: '',
        dob: '',
        age: '',
        gender: '',
        education: '',
        professions: [],
        otherProfession: '',
        groupName: 'Eco Tour - Mangrove Trail',
        prevPermit: 'PER-2023-089, PER-2024-012',
        division: '',
        district: '',
        upazila: '',
        thana: '',
        union: '',
        village: '',
        range: '',
        station: '',
        bloodGroup: '',
        phone: '',
        email: '',
        nidAttachment: '',
        birthCertAttachment: '',
        educationAttachment: ''
      });
    }} data-id="element-343" data-mp="2npsj1c7mn6wl">
          {currentStep === 0 && <div className="space-y-4" data-id="element-344" data-mp="3rlm3mnkgme4e">
              <div className="grid grid-cols-2 gap-4" data-id="element-345" data-mp="3mlhlyu7tfdcl">
                <div data-id="element-346" data-mp="3mmss30oqgx6v">
                  <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-347" data-mp="5cb4cq59ntyd">
                    Photo Upload *
                  </label>
                  <input type="file" accept="image/*" className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1B5E20]" data-id="element-348" data-mp="et0vfa0q6xr" />
                </div>
                <div data-id="element-349" data-mp="3ijctvzx0mos2">
                  <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-350" data-mp="3ijwbffir7b5k">
                    Full Name *
                  </label>
                  <input type="text" required value={formData.fullName} onChange={e => setFormData({
              ...formData,
              fullName: e.target.value
            })} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1B5E20]" data-id="element-351" data-mp="3dqjod9tq3cex" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4" data-id="element-352" data-mp="3ojiwph114nxs">
                <div data-id="element-353" data-mp="3oldmp9r5r68q">
                  <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-354" data-mp="2rt9xdws0fe8g">
                    Father's Name *
                  </label>
                  <input type="text" required value={formData.fatherName} onChange={e => setFormData({
              ...formData,
              fatherName: e.target.value
            })} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1B5E20]" data-id="element-355" data-mp="2vvdfh496ph2p" />
                </div>
                <div data-id="element-356" data-mp="25mo0i9a4yzqd">
                  <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-357" data-mp="20li4o2l6iwcm">
                    Spouse Name
                  </label>
                  <input type="text" value={formData.spouseName} onChange={e => setFormData({
              ...formData,
              spouseName: e.target.value
            })} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1B5E20]" data-id="element-358" data-mp="1vtqfnwho8yol" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4" data-id="element-359" data-mp="17y4eqofwf120">
                <div data-id="element-360" data-mp="379oqo5wetusg">
                  <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-361" data-mp="37a6ubnlejbae">
                    NID Number{' '}
                    <span className="text-xs text-gray-500 font-normal" data-id="element-362" data-mp="32c5bn3rw1dzg">
                      (Required if no Birth Cert)
                    </span>
                  </label>
                  <input type="text" value={formData.nid} onChange={e => setFormData({
              ...formData,
              nid: e.target.value
            })} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1B5E20]" data-id="element-363" data-mp="3fawi074xilb1" />
                </div>
                <div data-id="element-364" data-mp="2hn4g4zuxevx4">
                  <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-365" data-mp="2hnnpvtafae02">
                    Birth Certificate No.{' '}
                    <span className="text-xs text-gray-500 font-normal" data-id="element-366" data-mp="2mmh5psd6mh3e">
                      (Required if no NID)
                    </span>
                  </label>
                  <input type="text" value={formData.birthCertNo} onChange={e => setFormData({
              ...formData,
              birthCertNo: e.target.value
            })} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1B5E20]" data-id="element-367" data-mp="2co1dtn6bz3su" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4" data-id="element-368" data-mp="2cx23ttgh3lvx">
                <div data-id="element-369" data-mp="27za3cb1rirg4">
                  <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-370" data-mp="2801xrijmij4t">
                    Date of Birth *
                  </label>
                  <input type="date" required value={formData.dob} onChange={e => setFormData({
              ...formData,
              dob: e.target.value
            })} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1B5E20]" data-id="element-371" data-mp="2lpvok6eovdh6" />
                </div>
                <div data-id="element-372" data-mp="1sbulebuv3vpg">
                  <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-373" data-mp="1xd0bs311v6vt">
                    Age (Auto-calculated)
                  </label>
                  <input type="number" readOnly value={formData.age} className={`w-full px-3 py-2 border rounded-lg focus:outline-none bg-gray-50 ${formData.age && parseInt(formData.age) < 18 ? 'border-red-500 text-red-600' : 'border-gray-300 text-gray-600'}`} data-id="element-374" data-mp="225bdnbfcp2r0" />
                  {formData.age && parseInt(formData.age) < 18 && <p className="text-xs text-red-500 mt-1" data-id="element-375" data-mp="2l08kkuxosy2l">
                      Age must be 18 or above.
                    </p>}
                </div>
              </div>
              <div className="grid grid-cols-3 gap-4" data-id="element-376" data-mp="qqo4uxeet6c5">
                <div data-id="element-377" data-mp="j640ntufra84">
                  <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-378" data-mp="j6n7aeemyc6u">
                    Gender *
                  </label>
                  <select required value={formData.gender} onChange={e => setFormData({
              ...formData,
              gender: e.target.value
            })} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1B5E20]" data-id="element-379" data-mp="9f94nxre0w9h">
                    <option value="" data-id="element-380" data-mp="s460i62fnwtt">Select Gender</option>
                    <option value="male" data-id="element-381" data-mp="o4otv9b9stn2">Male</option>
                    <option value="female" data-id="element-382" data-mp="o688ywl6wyn9">Female</option>
                    <option value="other" data-id="element-383" data-mp="3cx105m4qfycf">Other</option>
                  </select>
                </div>
                <div data-id="element-384" data-mp="38tbccyvvtksg">
                  <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-385" data-mp="38t311bfeqnjd">
                    Blood Group
                  </label>
                  <select value={formData.bloodGroup} onChange={e => setFormData({
              ...formData,
              bloodGroup: e.target.value
            })} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1B5E20]" data-id="element-386" data-mp="1gsl82fjh4qfi">
                    <option value="" data-id="element-387" data-mp="1zi1a57at4qy1">Select Blood Group</option>
                    <option value="A+" data-id="element-388" data-mp="1ls69qxq13cvx">A+</option>
                    <option value="A-" data-id="element-389" data-mp="1lrejm2f7a7d1">A-</option>
                    <option value="B+" data-id="element-390" data-mp="1qqhggzl1179x">B+</option>
                    <option value="B-" data-id="element-391" data-mp="1qp6dh1zeo3jv">B-</option>
                    <option value="AB+" data-id="element-392" data-mp="2eg21do0qqelu">AB+</option>
                    <option value="AB-" data-id="element-393" data-mp="2efahhagia5be">AB-</option>
                    <option value="O+" data-id="element-394" data-mp="2j7tqdu490c9v">O+</option>
                    <option value="O-" data-id="element-395" data-mp="2j8mv6pjncrpd">O-</option>
                  </select>
                </div>
                <div data-id="element-396" data-mp="2skau36anco0t">
                  <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-397" data-mp="2okifocyknnnj">
                    Education
                  </label>
                  <input type="text" value={formData.education} onChange={e => setFormData({
              ...formData,
              education: e.target.value
            })} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1B5E20]" data-id="element-398" data-mp="32avmgbzq8wba" />
                </div>
              </div>
            </div>}
          {currentStep === 1 && <div className="space-y-4" data-id="element-399" data-mp="3v0cdb57p855e">
              <div className="grid grid-cols-2 gap-4" data-id="element-400" data-mp="3uzaxknfqmuhc">
                <div data-id="element-401" data-mp="3q1rlnu6iitsk">
                  <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-402" data-mp="3q2asaeqppvra">
                    Division *
                  </label>
                  <select required value={formData.division} onChange={e => setFormData({
              ...formData,
              division: e.target.value
            })} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1B5E20]" data-id="element-403" data-mp="u13ikfbc9oqk">
                    <option value="" data-id="element-404" data-mp="17zl6oe55p390">Select Division</option>
                    <option value="Khulna" data-id="element-405" data-mp="1mpiweyiqi10b">Khulna</option>
                    <option value="Barisal" data-id="element-406" data-mp="1mozgtogk3opg">Barisal</option>
                    <option value="Dhaka" data-id="element-407" data-mp="1hpwbe2wnzggy">Dhaka</option>
                  </select>
                </div>
                <div data-id="element-408" data-mp="1vpfgsny3cdpk">
                  <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-409" data-mp="1vqyzel77z0ui">
                    District *
                  </label>
                  <select required value={formData.district} onChange={e => setFormData({
              ...formData,
              district: e.target.value
            })} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1B5E20]" data-id="element-410" data-mp="1qp9lmxsm492q">
                    <option value="" data-id="element-411" data-mp="1rktqkbycsk5n">Select District</option>
                    <option value="Satkhira" data-id="element-412" data-mp="1wdmvazttnshq">Satkhira</option>
                    <option value="Khulna" data-id="element-413" data-mp="1wf7qmrnynfgn">Khulna</option>
                    <option value="Bagerhat" data-id="element-414" data-mp="1hp9v1qnpbnhf">Bagerhat</option>
                  </select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4" data-id="element-415" data-mp="1mnl4i0a33vz4">
                <div data-id="element-416" data-mp="18nadmx8ryhdy">
                  <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-417" data-mp="18ov70jjcrua8">
                    Upazila *
                  </label>
                  <select required value={formData.upazila} onChange={e => setFormData({
              ...formData,
              upazila: e.target.value
            })} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1B5E20]" data-id="element-418" data-mp="1dog74xa9e8oj">
                    <option value="" data-id="element-419" data-mp="23zhk7v58y6b">Select Upazila</option>
                    <option value="Shyamnagar" data-id="element-420" data-mp="3tb2bh72d9ik8">Shyamnagar</option>
                    <option value="Koyra" data-id="element-421" data-mp="3t9helaskxnj7">Koyra</option>
                    <option value="Dacope" data-id="element-422" data-mp="3p8xlqjf1h62v">Dacope</option>
                  </select>
                </div>
                <div data-id="element-423" data-mp="3ka454i4ggyyh">
                  <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-424" data-mp="3kambw8oquvkn">
                    Thana *
                  </label>
                  <select required value={formData.thana} onChange={e => setFormData({
              ...formData,
              thana: e.target.value
            })} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1B5E20]" data-id="element-425" data-mp="28w0dhilcbmwl">
                    <option value="" data-id="element-426" data-mp="126e2fwimnvwg">Select Thana</option>
                    <option value="Shyamnagar Thana" data-id="element-427" data-mp="174z480dj2nn0">Shyamnagar Thana</option>
                    <option value="Koyra Thana" data-id="element-428" data-mp="1c30mwk71kkxy">Koyra Thana</option>
                    <option value="Dacope Thana" data-id="element-429" data-mp="1c1fvh3g0xi4b">Dacope Thana</option>
                  </select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4" data-id="element-430" data-mp="1l3p0xewfqlau">
                <div data-id="element-431" data-mp="1l35uovg5dlcn">
                  <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-432" data-mp="1l1mdn2mo369t">
                    Union *
                  </label>
                  <input type="text" required value={formData.union} onChange={e => setFormData({
              ...formData,
              union: e.target.value
            })} placeholder="Enter Union name" className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1B5E20]" data-id="element-433" data-mp="3muk1sl5i1aof" />
                </div>
                <div data-id="element-434" data-mp="28sb4l96se6dv">
                  <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-435" data-mp="23w2x8hytzhh9">
                    Village *
                  </label>
                  <input type="text" required value={formData.village} onChange={e => setFormData({
              ...formData,
              village: e.target.value
            })} placeholder="Enter Village name" className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1B5E20]" data-id="element-436" data-mp="2im1ri9n9m8rg" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4" data-id="element-437" data-mp="3nsry3mg5sfs4">
                <div data-id="element-438" data-mp="3rtbsii9cl5ak">
                  <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-439" data-mp="3rstv3iay8l12">
                    Range *
                  </label>
                  <select required value={formData.range} onChange={e => setFormData({
              ...formData,
              range: e.target.value,
              station: ''
            })} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1B5E20]" data-id="element-440" data-mp="je1t5hwuee6h">
                    <option value="" data-id="element-441" data-mp="303qse6ypniqj">Select Range</option>
                    <option value="Satkhira Range" data-id="element-442" data-mp="15mh0kvsg9x1u">Satkhira Range</option>
                    <option value="Khulna Range" data-id="element-443" data-mp="15ns6p29dbgw4">Khulna Range</option>
                    <option value="Chandpai Range" data-id="element-444" data-mp="10ne3bxipov4f">Chandpai Range</option>
                    <option value="Sarankhola Range" data-id="element-445" data-mp="vv2vm8hqc51b">Sarankhola Range</option>
                  </select>
                </div>
                <div data-id="element-446" data-mp="ruak18ldxo1v">
                  <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-447" data-mp="rsq00cwp5ni9">
                    Station
                  </label>
                  <select value={formData.station} onChange={e => setFormData({
              ...formData,
              station: e.target.value
            })} disabled={!formData.range} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1B5E20] disabled:bg-gray-100 disabled:text-gray-400" data-id="element-448" data-mp="hw3h3tnxl6iv">
                    <option value="" data-id="element-449" data-mp="3l6jb42behio9">
                      {formData.range ? 'Select Station' : 'Select Range first'}
                    </option>
                    {formData.range && stationsByRange[formData.range]?.map(station => <option key={station} value={station} data-id="element-450" data-mp="2tuyfb89qiw3">
                          {station}
                        </option>)}
                  </select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4" data-id="element-451" data-mp="378kzh4ogs0cg">
                <div data-id="element-452" data-mp="3b94x09cy95z4">
                  <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-453" data-mp="3b8n49mphx9vy">
                    Phone Number *
                  </label>
                  <input type="tel" required value={formData.phone} onChange={e => setFormData({
              ...formData,
              phone: e.target.value
            })} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1B5E20]" data-id="element-454" data-mp="3g9j8609ukw1d" />
                </div>
                <div data-id="element-455" data-mp="fzv6828xujny">
                  <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-456" data-mp="b723tl0xznf1">
                    Email Address
                  </label>
                  <input type="email" value={formData.email} onChange={e => setFormData({
              ...formData,
              email: e.target.value
            })} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1B5E20]" data-id="element-457" data-mp="puvdyq79dxq8" />
                </div>
              </div>
            </div>}
          {currentStep === 2 && <div className="space-y-6" data-id="element-458" data-mp="2mipnx6855wno">
              <div data-id="element-459" data-mp="2mjqxf69ieb3a">
                <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-460" data-mp="2mj92ccyl22wy">
                  Profession(s) *
                </label>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3" data-id="element-461" data-mp="2csweaark7rm2">
                  {professionOptions.map(prof => <label key={prof} className="flex items-center gap-2 p-3 border border-gray-200 rounded-lg cursor-pointer hover:bg-gray-50" data-id="element-462" data-mp="2hqpjb0h4mfkr">
                      <input type="checkbox" checked={formData.professions.includes(prof)} onChange={() => handleProfessionChange(prof)} className="w-4 h-4 text-[#1B5E20] rounded focus:ring-[#1B5E20]" data-id="element-463" data-mp="27v4ktcb9nxfr" />
                      <span className="text-sm text-gray-700" data-id="element-464" data-mp="3o5qdp1qi6pp8">{prof}</span>
                    </label>)}
                </div>
                {formData.professions.includes('Others') && <div className="mt-3" data-id="element-465" data-mp="yrbjtdtfhx8p">
                    <input type="text" placeholder="Please specify other profession" value={formData.otherProfession} onChange={e => setFormData({
              ...formData,
              otherProfession: e.target.value
            })} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1B5E20]" data-id="element-466" data-mp="13rg3fcpmv835" />
                  </div>}
              </div>
              <div className="grid grid-cols-2 gap-4" data-id="element-467" data-mp="1wgemcc1kye9i">
                <div data-id="element-468" data-mp="3t9q70cudygsi">
                  <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-469" data-mp="3ta8anujdnxag">
                    Conservation Group / Association
                  </label>
                  <input type="text" readOnly value={formData.groupName} className="w-full px-3 py-2 border border-gray-200 bg-gray-50 rounded-lg text-gray-600 focus:outline-none" data-id="element-470" data-mp="2fd5cempyg0uk" />
                  <p className="text-xs text-gray-500 mt-1" data-id="element-471" data-mp="26iqvygtouk9v">
                    Tagged conservation group (Read-only)
                  </p>
                </div>
                <div data-id="element-472" data-mp="2yaq2fx4gh5gr">
                  <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-473" data-mp="2yb863etg6lyp">
                    Previous Permit Records
                  </label>
                  <input type="text" readOnly value={formData.prevPermit} className="w-full px-3 py-2 border border-gray-200 bg-gray-50 rounded-lg text-gray-600 focus:outline-none" data-id="element-474" data-mp="2patgi39gakpe" />
                  <p className="text-xs text-gray-500 mt-1" data-id="element-475" data-mp="18xct1thhkr8s">
                    Past permits for this beneficiary (Read-only)
                  </p>
                </div>
              </div>
            </div>}
          {currentStep === 3 && <div className="space-y-4" data-id="element-476" data-mp="1fcthxj7jcy9o">
              <div data-id="element-477" data-mp="11dkcnekvbokh">
                <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-478" data-mp="11e3n6a86vaoh">
                  NID Copy
                </label>
                <input type="file" accept="image/*,application/pdf" className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1B5E20]" data-id="element-479" data-mp="16b3pca1x3tab" />
              </div>
              <div data-id="element-480" data-mp="invoc4biv6e3">
                <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-481" data-mp="ionhz9lk6u1q">
                  Birth Certificate Copy
                </label>
                <input type="file" accept="image/*,application/pdf" className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1B5E20]" data-id="element-482" data-mp="8owkcz1pbyxf" />
              </div>
              <div data-id="element-483" data-mp="3q9oduoyaq5qu">
                <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-484" data-mp="3l8il4r4myihb">
                  Education Certificate
                </label>
                <input type="file" accept="image/*,application/pdf" className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1B5E20]" data-id="element-485" data-mp="415a62cp9jb3" />
              </div>
            </div>}
          {currentStep === 4 && <div className="space-y-6" data-id="element-486" data-mp="804naa8l4mdm">
              <div className="bg-gray-50 rounded-lg p-4" data-id="element-487" data-mp="mr4u6atoo87n">
                <div className="flex items-center justify-between mb-3" data-id="element-488" data-mp="mspqo5zk6185">
                  <h4 className="font-semibold text-gray-900" data-id="element-489" data-mp="hsaaz7rt0hn0">
                    Personal Information
                  </h4>
                  <button onClick={() => setCurrentStep(0)} className="text-sm text-[#1B5E20] hover:underline" data-id="element-490" data-mp="vp7094s9xr1s">
                    Edit
                  </button>
                </div>
                <div className="grid grid-cols-2 gap-3 text-sm" data-id="element-491" data-mp="2q6qb1jl5wjmo">
                  <div data-id="element-492" data-mp="2uyi7gar01jkq">
                    <span className="text-gray-600" data-id="element-493" data-mp="2uy8eceanqwat">Name:</span>{' '}
                    <span className="font-medium" data-id="element-494" data-mp="2zxl9dmbon9oh">
                      {formData.fullName || 'Not provided'}
                    </span>
                  </div>
                  <div data-id="element-495" data-mp="22bdvfltho4zg">
                    <span className="text-gray-600" data-id="element-496" data-mp="22c5qmvj6c0p7">Father's Name:</span>{' '}
                    <span className="font-medium" data-id="element-497" data-mp="2795y9avo9bif">
                      {formData.fatherName || 'Not provided'}
                    </span>
                  </div>
                  <div data-id="element-498" data-mp="2g9ubi3d8r7u9">
                    <span className="text-gray-600" data-id="element-499" data-mp="2gb5fo4almhlw">NID:</span>{' '}
                    <span className="font-medium" data-id="element-500" data-mp="3nnxfcrndawcf">
                      {formData.nid || 'Not provided'}
                    </span>
                  </div>
                  <div data-id="element-501" data-mp="3rqu28ujxxem6">
                    <span className="text-gray-600" data-id="element-502" data-mp="1g15t1pwmrqbx">Birth Cert:</span>{' '}
                    <span className="font-medium" data-id="element-503" data-mp="1g3i9tzqnjune">
                      {formData.birthCertNo || 'Not provided'}
                    </span>
                  </div>
                  <div data-id="element-504" data-mp="177sp7sx134b2">
                    <span className="text-gray-600" data-id="element-505" data-mp="176zg4lalk2pr">DOB:</span>{' '}
                    <span className="font-medium" data-id="element-506" data-mp="128xv3utm1tbn">
                      {formData.dob || 'Not provided'}
                    </span>
                  </div>
                  <div data-id="element-507" data-mp="sbt9px7la1vw">
                    <span className="text-gray-600" data-id="element-508" data-mp="sbjt308fp72v">Age:</span>{' '}
                    <span className="font-medium" data-id="element-509" data-mp="oa80clu9k5qg">
                      {formData.age || 'Not provided'}
                    </span>
                  </div>
                  <div data-id="element-510" data-mp="jcg6vndz0bk5">
                    <span className="text-gray-600" data-id="element-511" data-mp="jd7vufd2bb1g">Blood Group:</span>{' '}
                    <span className="font-medium" data-id="element-512" data-mp="2inqsth9fsy4w">
                      {formData.bloodGroup || 'Not provided'}
                    </span>
                  </div>
                </div>
              </div>
              <div className="bg-gray-50 rounded-lg p-4" data-id="element-513" data-mp="2ws7zkur7ciwn">
                <div className="flex items-center justify-between mb-3" data-id="element-514" data-mp="31l13jgeujn7o">
                  <h4 className="font-semibold text-gray-900" data-id="element-515" data-mp="2nt2zvl3wmjiw">
                    Address & Contact
                  </h4>
                  <button onClick={() => setCurrentStep(1)} className="text-sm text-[#1B5E20] hover:underline" data-id="element-516" data-mp="2sro2fr6mpfai">
                    Edit
                  </button>
                </div>
                <div className="grid grid-cols-2 gap-3 text-sm" data-id="element-517" data-mp="3kikeipkv7fwq">
                  <div data-id="element-518" data-mp="36kdmvr6iec5i">
                    <span className="text-gray-600" data-id="element-519" data-mp="36j2k9uosvafz">Division:</span>{' '}
                    <span className="font-medium" data-id="element-520" data-mp="3bhvzbrjqj9i9">
                      {formData.division || 'Not provided'}
                    </span>
                  </div>
                  <div data-id="element-521" data-mp="1v6rxc4wqm8jg">
                    <span className="text-gray-600" data-id="element-522" data-mp="2041njmryao8c">District:</span>{' '}
                    <span className="font-medium" data-id="element-523" data-mp="2015rk3zny9mj">
                      {formData.district || 'Not provided'}
                    </span>
                  </div>
                  <div data-id="element-524" data-mp="1nkefh23qgrxx">
                    <span className="text-gray-600" data-id="element-525" data-mp="1nn0lgxaz6ze1">Upazila:</span>{' '}
                    <span className="font-medium" data-id="element-526" data-mp="21k5o0nbhdg4j">
                      {formData.upazila || 'Not provided'}
                    </span>
                  </div>
                  <div data-id="element-527" data-mp="18vi9dal8lozy">
                    <span className="text-gray-600" data-id="element-528" data-mp="18x1pn16w801q">Thana:</span>{' '}
                    <span className="font-medium" data-id="element-529" data-mp="14uoiq9getw3d">
                      {formData.thana || 'Not provided'}
                    </span>
                  </div>
                  <div data-id="element-530" data-mp="1in4i90a3rbzj">
                    <span className="text-gray-600" data-id="element-531" data-mp="1dv38pm4bjzd7">Union:</span>{' '}
                    <span className="font-medium" data-id="element-532" data-mp="1dts79sycj3p9">
                      {formData.union || 'Not provided'}
                    </span>
                  </div>
                  <div data-id="element-533" data-mp="l77firdykpl6">
                    <span className="text-gray-600" data-id="element-534" data-mp="l5nuknhcxqd2">Village:</span>{' '}
                    <span className="font-medium" data-id="element-535" data-mp="ldsox2he5up">
                      {formData.village || 'Not provided'}
                    </span>
                  </div>
                  <div data-id="element-536" data-mp="4o0o17yd28wn">
                    <span className="text-gray-600" data-id="element-537" data-mp="9lk48demcvr8">Station:</span>{' '}
                    <span className="font-medium" data-id="element-538" data-mp="9nn2fkpxdv71">
                      {formData.station || 'Not provided'}
                    </span>
                  </div>
                  <div data-id="element-539" data-mp="jiop89wqal6g">
                    <span className="text-gray-600" data-id="element-540" data-mp="jj7zr5k1u7ag">Phone:</span>{' '}
                    <span className="font-medium" data-id="element-541" data-mp="njqrz0putfd6">
                      {formData.phone || 'Not provided'}
                    </span>
                  </div>
                </div>
              </div>
              <div className="bg-gray-50 rounded-lg p-4" data-id="element-542" data-mp="x9tkz0wz6tbi">
                <div className="flex items-center justify-between mb-3" data-id="element-543" data-mp="129xxygyod5wz">
                  <h4 className="font-semibold text-gray-900" data-id="element-544" data-mp="127v23gaucikc">
                    Professional Details
                  </h4>
                  <button onClick={() => setCurrentStep(2)} className="text-sm text-[#1B5E20] hover:underline" data-id="element-545" data-mp="2rqtl8k2jlzse">
                    Edit
                  </button>
                </div>
                <div className="grid grid-cols-1 gap-3 text-sm" data-id="element-546" data-mp="2htowqdl85s8f">
                  <div data-id="element-547" data-mp="2cuc8boepgd3q">
                    <span className="text-gray-600" data-id="element-548" data-mp="2cv46n6zosoxp">Professions:</span>{' '}
                    <span className="font-medium capitalize" data-id="element-549" data-mp="3ai47gnxbpody">
                      {formData.professions.length > 0 ? formData.professions.map(p => p === 'Others' ? formData.otherProfession : p).join(', ') : 'Not provided'}
                    </span>
                  </div>
                  <div data-id="element-550" data-mp="3t9djb3ukgp8p">
                    <span className="text-gray-600" data-id="element-551" data-mp="3t9x4qujfdvrh">Conservation Group:</span>{' '}
                    <span className="font-medium" data-id="element-552" data-mp="3of9b6h9keb61">{formData.groupName}</span>
                  </div>
                </div>
              </div>
            </div>}
        </WizardForm>}

      {activeTab === 'approval' && <DataTable columns={approvalColumns} data={pendingApprovals} onView={row => console.log('View', row)} onApprove={row => alert(`Approved: ${row.name}`)} searchPlaceholder="Search pending approvals..." data-id="element-553" data-mp="1dzwrxi4kqgwn" />}
    </div>;
}